import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Dino from 'react-chrome-dino';

const PageStyles = styled.div`
  padding: 16rem 0 10rem;
  min-height: 80vh;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.4rem;
  color: var(--highlight-blue);
  opacity: 0.7;
  margin-bottom: 3rem;
  transition: opacity 0.2s ease;
  &:hover { opacity: 1; }
  &::before { content: '←'; }
`;

const GameSection = styled.section`
  max-width: 860px;
  margin: 0 auto;

  .game__header {
    display: flex;
    align-items: baseline;
    gap: 1.2rem;
    margin-bottom: 1.6rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--gray-2);
  }
  .game__title {
    font-size: 3.2rem;
    font-family: 'Montserrat Bold';
    color: var(--gray-1);
  }
  .game__badge {
    font-size: 1.1rem;
    padding: 0.3em 0.9em;
    border-radius: 20px;
    background: var(--highlight-blue);
    color: var(--dark-bg);
    text-transform: uppercase;
    letter-spacing: 0.08rem;
  }
`;

const ScoreBar = styled.div`
  display: flex;
  gap: 3rem;
  margin-bottom: 1.6rem;

  .score-item {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }
  .score-label {
    font-size: 1.1rem;
    text-transform: uppercase;
    letter-spacing: 0.1rem;
    color: var(--gray-1);
    opacity: 0.45;
  }
  .score-value {
    font-size: 2.8rem;
    font-family: 'Montserrat Bold';
    color: var(--highlight-blue);
    line-height: 1;
  }
`;

const GameCard = styled.div`
  background: #f7f7f7;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid var(--gray-2);
  margin-bottom: 1.6rem;

  iframe, canvas {
    display: block;
    width: 100% !important;
    border: none;
  }
`;

const GameOverBanner = styled.div`
  background: var(--deep-dark);
  border: 1px solid var(--highlight-blue);
  border-radius: 10px;
  padding: 1.6rem 2.4rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.6rem;

  p {
    font-size: 1.5rem;
    color: var(--gray-1);
    opacity: 0.8;
  }
`;

const Btn = styled.button`
  font-size: 1.4rem;
  padding: 0.7em 2em;
  background: transparent;
  color: var(--highlight-blue);
  border: 2px solid var(--highlight-blue);
  border-radius: 8px;
  cursor: pointer;
  font-family: 'RobotoMono Regular';
  transition: background 0.2s ease, color 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: var(--highlight-blue);
    color: var(--dark-bg);
  }
`;

const InstructionsBox = styled.div`
  background: var(--deep-dark);
  border-left: 3px solid var(--highlight-blue);
  border-radius: 0 10px 10px 0;
  padding: 1.6rem 2.4rem;
  margin-top: 1.2rem;

  h4 {
    font-size: 1.5rem;
    color: var(--highlight-blue);
    margin-bottom: 0.8rem;
  }
  ul {
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  li {
    font-size: 1.4rem;
    color: var(--gray-1);
    opacity: 0.75;
    &::before {
      content: '→ ';
      color: var(--highlight-blue);
    }
  }
`;

export default function DinoGame() {
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => Number(localStorage.getItem('dino-highScore') || 0));
  const [isGameOver, setIsGameOver] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const handleGameOver = (finalScore) => {
    setScore(finalScore);
    setIsGameOver(true);
    if (finalScore > highScore) {
      setHighScore(finalScore);
      localStorage.setItem('dino-highScore', String(finalScore));
    }
  };

  const resetGame = () => {
    setIsGameOver(false);
    setScore(0);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.keyCode === 32 || e.keyCode === 38) e.preventDefault();
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <PageStyles>
      <Helmet><title>Dino Run | Anas Qazi</title></Helmet>
      <div className="container">
        <BackLink to="/games">All Games</BackLink>
      </div>

      <GameSection>
        <div className="game__header">
          <span className="game__title">🦕 Dino Run</span>
          <span className="game__badge">Classic</span>
        </div>

        <ScoreBar>
          <div className="score-item">
            <span className="score-label">Score</span>
            <span className="score-value">{score}</span>
          </div>
          <div className="score-item">
            <span className="score-label">Best</span>
            <span className="score-value">{highScore}</span>
          </div>
        </ScoreBar>

        <GameCard>
          <Dino onGameOver={handleGameOver} setScore={setScore} />
        </GameCard>

        {isGameOver && (
          <GameOverBanner>
            <p>Game over — Score: <strong>{score}</strong> · Best: <strong>{highScore}</strong></p>
            <Btn onClick={resetGame}>Play Again</Btn>
          </GameOverBanner>
        )}

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <Btn onClick={() => setShowInstructions((v) => !v)}>
            {showInstructions ? 'Hide Tips' : 'How to Play'}
          </Btn>
        </div>

        {showInstructions && (
          <InstructionsBox>
            <h4>How to Play</h4>
            <ul>
              <li>Press <strong>Space</strong> or <strong>↑</strong> to jump</li>
              <li>Press <strong>↓</strong> to duck under pterodactyls</li>
              <li>Avoid cacti and flying obstacles</li>
              <li>Speed increases the longer you survive</li>
            </ul>
          </InstructionsBox>
        )}
      </GameSection>
    </PageStyles>
  );
}
